import React, { useState } from 'react';
import { Row, Col, Card, Badge, Button, Nav } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import moment from 'moment';
import { EconomicIndicators } from '../../../constants/realEstate';
import {
  GoogleMap,
  RequestLoading,
  RequestResult,
  ShowMoreText,
  Tooltip,
} from '../../../components';
import { useAxiosQuery, useConfirmModal } from '../../../hooks';
import LineChartCard from './partials/LineChartCard';
import LineChartCardHomesSold from './partials/LineChartCardHomesSold';
import LineChartCardMedianDayOnMarket from './partials/LineChartCardMedianDayOnMarket';
import LineChartCardMarketRents from './partials/LineChartCardMarketRents';
import DoughnutChartCard from './partials/DoughnutChartCard';
import DoughnutChartCardRace from './partials/DoughnutChartCardRace';
import HalfDoughnutChartCard from './partials/HalfDoughnutChartCard';
import HalfDoughnutChartCardVoting from './partials/HalfDoughnutChartCardVoting';
import InfoPropertyDataCard from './partials/InfoPropertyDataCard';
import LineChartCardSaleToListPrice from './partials/LineChartCardSaleToListPrice';
import LineChartCardHomeSoldAboveList from './partials/LineChartCardHomeSoldAboveList';
import Constants from '../../../constants';
import ScoreChartCard from './partials/ScoreChartCard';
import LineChartCardMarketInventory from './partials/LineChartCardMarketInventory';
import BarChartCard from './partials/BarChartCard';
import LineChartCardMarketPriceIncrease from './partials/LineChartCardMarketPriceIncrease';
import LineChartCardMarketPricePerSquare from './partials/LineChartCardMarketPricePerSquare';

import WordCloudListing from './partials/WordCloudListing';
import { Routes as AppRoutes } from '../../../router/routeMapping';
import MortgageCalculator from './partials/MortgageCalculator';

function formatPrice(price) {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(2)}M`;
  }
  if (price >= 1000) {
    return `${(price / 1000).toFixed(0)}K`;
  }
  return price.toString();
}

function calculateMonthlyMortgage(
  homePrice,
  downPaymentPercent,
  loanTermYears,
  interestRate
) {
  // Down payment hesaplama
  const downPayment = homePrice * (downPaymentPercent / 100);
  // Mortgage miktarını hesapla
  const loanAmount = homePrice - downPayment;
  // Aylık faiz oranını hesapla
  const monthlyInterestRate = interestRate / 100 / 12;
  // Toplam ödeme sayısını hesapla (ay olarak)
  const numberOfPayments = loanTermYears * 12;

  // Aylık ödeme miktarını hesapla
  const rawMonthlyPayment =
    loanAmount *
    (monthlyInterestRate /
      (1 - (1 + monthlyInterestRate) ** -numberOfPayments));

  // Aylık ödeme miktarını yuvarla ve formatla
  const monthlyPayment = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rawMonthlyPayment);

  return monthlyPayment;
}

const downPaymentPercent = 20; // Peşinat yüzdesi
const loanTermYears = 30; // Kredi süresi (yıl olarak)
const interestRate = 7.5; // Yıllık faiz oranı

function getScoreColor(score) {
  if (score >= 0 && score <= 39) {
    return 'Danger';
  }
  if (score >= 40 && score <= 59) {
    return 'Warning';
  }
  if (score >= 60 && score <= 79) {
    return 'Primary';
  }
  return 'Success';
}

const getMapPin = (type, text) => {
  let svgString = '';
  let width = 48;
  let height = 48;

  switch (type) {
    case 'Bus':
      width = 128;
      height = 128;
      svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" fill-rule="evenodd" clip-rule="evenodd" image-rendering="optimizeQuality" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" viewBox="0 0 256 256" id="BusStationLocation"><polygon fill="none" points="0 0 85 0 85 85 0 85"></polygon><path fill="#404040" d="M31 37l24 0c2,0 2,3 0,3l-24 0c-2,0 -2,-3 0,-3z" class="color427db7 svgShape"></path><path fill="#334349" d="M52 30c1 0 2 1 2 2 0 0-1 1-2 1-1 0-2-1-2-1 0-1 1-2 2-2zm-18 0l0 0c1 0 2 1 2 2 0 0-1 1-2 1-1 0-2-1-2-1 0-1 1-2 2-2zm6 1l0 0 6 0c0 0 0 1 0 1 0 0 0 0 0 0l-6 0c0 0 0 0 0 0 0 0 0-1 0-1zm0 3l0 0 6 0c0 0 0 0 0 0 0 0 0 1 0 1l-6 0c0 0 0-1 0-1 0 0 0 0 0 0zm0-5l0 0 6 0c0 0 0 1 0 1 0 0 0 0 0 0l-6 0c0 0 0 0 0 0 0 0 0-1 0-1zm-7-1l0 0 3 0c1 0 2 0 2-1l0-1c0 0 0-1 1-1l8 0c1 0 1 1 1 1l0 1c0 1 1 1 2 1l3 0c1 0 2 1 2 2l0 5c0 1 0 1-1 1l-22 0c-1 0-1 0-1-1l0-5c0-1 1-2 2-2zM50 41l3 0c0 0 1 0 1 0l0 3c0 0-1 0-1 0l-2 0c-1 0-2 0-2 0l0-3c0 0 1 0 1 0zm-17 0l0 0 3 0c0 0 0 0 0 0l0 3c0 0 0 0-1 0l-2 0c0 0-1 0-1 0l0-3c0 0 1 0 1 0z" class="color3a3b53 svgShape"></path><path fill="#334349" d="M41 16l4 0c0,0 0,2 0,2l-4 0c0,0 0,-2 0,-2zm-5 -1l0 0 14 0c1,0 3,1 3,3l0 9c0,1 0,1 0,1 0,0 0,0 0,0 0,0 -1,0 -1,0l-1 0c0,0 0,0 0,-1l0 -6c0,-1 0,-2 -1,-2l-5 0c-1,0 -1,1 -1,2l0 3 -2 0 0 -3c0,-1 0,-2 -1,-2l-5 0c-1,0 -1,1 -1,2l0 6c0,1 0,1 0,1l-1 0c-1,0 -1,0 -1,0 0,0 0,0 0,0 0,0 0,0 0,-1l0 -9c0,-2 2,-3 3,-3z" class="color3a3b53 svgShape"></path><path fill="#404040" fill-rule="nonzero" d="M32 21l-1 0c0 0 0 0 0 0l0 0 0-1c0 0 0 0 0-1 0 0 1 0 1 0l0 0c0 0 0 0 0 0l0 0c0-1 0-1 0-1l0 0c-1 0-1 1-1 1-1 0-1 1-1 1l0 1 0 0c0 0 0 0 0 0-1 0-1 0-1 0l0 0 0 3 0 0c0 1 1 1 1 1l2 0c0 0 0 0 0-1l0-3c0 0 0 0 0 0zM56 21c0 0 0 0 0 0l0 0 0-1c0 0 0-1-1-1 0 0-1-1-1-1l0 0c0 0 0 0 0 1l0 0c0 0 0 0 0 0l0 0c0 0 1 0 1 0 0 1 0 1 0 1l0 1 0 0c0 0 0 0 0 0l-1 0c0 0 0 0 0 0l0 3c0 1 0 1 0 1l2 0c0 0 1 0 1-1l0 0 0-3 0 0c0 0-1 0-1 0z" class="color427db7 svgShape"></path><path fill="#404040" d="M69 48l-24 36c0,1 -1,1 -2,1 -1,0 -2,0 -2,-1l-24 -36c-3,-5 -4,-10 -4,-17 0,-17 13,-31 30,-31 17,0 30,14 30,31 0,7 -1,12 -4,17zm-26 -41l0 0c-12,0 -22,11 -22,24 0,12 10,23 22,23 13,0 23,-11 23,-23 0,-13 -10,-24 -23,-24z" class="color427db7 svgShape"></path></svg>`;
      break;
    case 'own':
      width = 64;
      height = 48;
      svgString = `<svg width="64" height="48" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg">
     <path fill="#377dff" d="M0,46.4V4.8C0,2.2,2.7,0,6,0h52c3.3,0,6,2.2,6,4.8v25.6c0,2.6-2.7,4.8-6,4.8H18.8L3.4,47.5
       C2.2,48.5,0,47.8,0,46.4z"/>
     <text x="52%" y="40%" dominant-baseline="middle" text-anchor="middle" fill="#FFFFFF" style="font-family:Arial; font-weight:bold;">${text}</text>
     </svg>
     `;
      break;
    case 'comp':
      width = 64;
      height = 48;
      svgString = `<svg width="64" height="48" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg">
   <path fill="#00985A" d="M0,46.4V4.8C0,2.2,2.7,0,6,0h52c3.3,0,6,2.2,6,4.8v25.6c0,2.6-2.7,4.8-6,4.8H18.8L3.4,47.5
     C2.2,48.5,0,47.8,0,46.4z"/>
   <text x="52%" y="40%" dominant-baseline="middle" text-anchor="middle" fill="#FFFFFF" style="font-family:Arial; font-weight:bold;">${text}</text>
   </svg>
   `;
      break;

    case 'score':
      {
        const color = Constants.Colors.Theme[getScoreColor(text)];
        width = 64;
        height = 48;
        svgString = `<svg width="64" height="48" viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg">
   <path fill="${color}" d="M0,46.4V4.8C0,2.2,2.7,0,6,0h52c3.3,0,6,2.2,6,4.8v25.6c0,2.6-2.7,4.8-6,4.8H18.8L3.4,47.5
     C2.2,48.5,0,47.8,0,46.4z"/>
   <text x="52%" y="40%" dominant-baseline="middle" text-anchor="middle" fill="#FFFFFF" style="font-family:Arial; font-weight:bold;">${text}</text>
   </svg>
   `;
      }

      break;

    case 'Bus1':
      {
        const color = '#ff0000';
        const textColor = '#ffffff';
        width = 256;
        height = 48;
        svgString = `<svg width="256" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path
    d="M24.0233 41.1847C20.6947 38.3378 10 28.5994 10 21C10 12 16 6 25 6C34 6 40 13.5 40 21C40 27.3634 29.2018 38.0462 25.929 41.1378C25.3952 41.6421 24.5813 41.662 24.0233 41.1847ZM30 19C30 21.7614 27.7614 24 25 24C22.2386 24 20 21.7614 20 19C20 16.2386 22.2386 14 25 14C27.7614 14 30 16.2386 30 19Z"
    fill="${color}"
  />
  ${
    text
      ? `<text x="52%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="${textColor}" style="font-family:Arial; font-weight:bold;">${text}</text>`
      : ''
  }
</svg>`;
      }

      break;

    default:
      {
        const color = Constants.Colors.Theme.Primary;
        const textColor = '#ffffff';
        width = 48;
        height = 48;
        svgString = `<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M24.0233 41.1847C20.6947 38.3378 10 28.5994 10 21C10 12 16 6 25 6C34 6 40 13.5 40 21C40 27.3634 29.2018 38.0462 25.929 41.1378C25.3952 41.6421 24.5813 41.662 24.0233 41.1847ZM30 19C30 21.7614 27.7614 24 25 24C22.2386 24 20 21.7614 20 19C20 16.2386 22.2386 14 25 14C27.7614 14 30 16.2386 30 19Z"
                      fill="${color}"
                    />
                    ${
                      text
                        ? `<text x="52%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="${textColor}" style="font-family:Arial; font-weight:bold;">${text}</text>`
                        : ''
                    }
                  </svg>`;
      }
      break;
  }

  return { svgString, width, height };
};

const aiScore = 25;

const ZIP_MEDIAN_HOME_PRICE = 1690000;
const ZIP_MEDIAN_DAYS_ON_MARKET = 36;

function getDateDifferenceAndColor(dateString) {
  const today = new Date();
  const listingDate = new Date(dateString);

  const timeDifference = Math.abs(today - listingDate);
  const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));

  let badgeColor;
  let badgeText;
  const days = daysDifference;

  if (daysDifference === 0) {
    badgeColor = 'info';
    badgeText = 'New';
  } else if (daysDifference <= ZIP_MEDIAN_DAYS_ON_MARKET) {
    badgeColor = 'success';
    badgeText = `${daysDifference} days ago`;
  } else {
    badgeColor = 'danger';
    badgeText = `${daysDifference} days ago`;
  }

  return {
    badgeColor,
    badgeText,
    days,
  };
}

function getEquityAdvice(equityValue, propertyValue) {
  const equityPercentage = (equityValue / propertyValue) * 100;

  let color = 'danger';
  let advice = 'Low Equity';

  if (equityPercentage >= 20 && equityPercentage < 40) {
    color = 'warning';
    advice = 'Med. Equity';
  } else if (equityPercentage >= 40 && equityPercentage < 60) {
    color = 'info';
    advice = 'Good Equity';
  } else if (equityPercentage >= 60) {
    color = 'success';
    advice = 'High Equity';
  }

  return {
    color,
    advice,
    equityPercentage,
  };
}
function getInvestmentAdvice(value) {
  let badgeColor;
  let badgeText;
  let recommendation;

  if (value >= 1 && value <= 40) {
    badgeColor = 'danger';
    badgeText = 'danger';
    recommendation = 'We do not recommend investing in this property.';
  } else if (value >= 41 && value <= 69) {
    badgeColor = 'warning';
    badgeText = 'warning';
    recommendation = 'Proceed with caution if considering this property.';
  } else if (value >= 70 && value <= 90) {
    badgeColor = 'success';
    badgeText = 'success';
    recommendation = 'This property is a good investment opportunity.';
  } else if (value >= 91 && value <= 100) {
    badgeColor = 'info';
    badgeText = 'info';
    recommendation = 'This property is an excellent investment opportunity.';
  }

  return {
    badgeColor,
    badgeText,
    recommendation,
  };
}
function getRiskColor(riskLevel) {
  switch (riskLevel) {
    case 'Extreme':
      return 'danger';
    case 'Major':
      return 'danger';
    case 'Minor':
      return 'warning';
    case 'Moderate':
      return 'success';
    case 'Severe':
      return 'danger';
    default:
      return 'info';
  }
}

// Örnek Kullanım

function getCompetitiveness(value) {
  const result = {
    text: 'N/A',
    color: 'info',
  };

  switch (value) {
    case 'Not Very Competitive':
      result.text =
        'Homes sit longer on the market with reduced buyer interest.';
      result.color = 'info';
      break;
    case 'Somewhat Competitive':
      result.text = 'Homes sell at or slightly below list prices.';
      result.color = 'warning';
      break;
    case 'Most Competitive':
      result.text =
        'Buyers vastly outnumber listings, leading to bidding wars.';
      result.color = 'danger';
      break;
    case 'Very Competitive':
      result.text = 'homes selling quickly, often at listing prices.';
      result.color = 'danger';
      break;
    default:
      break;
  }

  return result;
}

const advice = getInvestmentAdvice(aiScore);

const equity = getEquityAdvice(619999, 750000);

const mapTypes = [
  {
    titleKey: 'price',
  },
  {
    titleKey: 'score',
  },
];

function Info() {
  const { id } = useParams();
  const { formatMessage } = useIntl();
  const { confirm } = useConfirmModal({ confirmVariant: 'primary' });
  const [activeMapType, setActiveMapType] = useState(mapTypes[0].titleKey);

  const {
    data: apiData,
    isLoading: apiLoading,
    error: apiError,
  } = useAxiosQuery({
    url: '/homes/detail',
    preventFetch: !id,
    params: {
      id,
      distance: 1,
    },
    select: (data) => {
      const modifiedData = data;

      modifiedData[0].gtfs_stops = modifiedData[0].gtfs_stops.map((item) => ({
        ...item,
        lat: Number(item.stop_lat),
        lng: Number(item.stop_lon),
        customIcon: getMapPin(item.stop_type, item.stop_type),
      }));

      return modifiedData;
    },
  });

  const getPriceOrScorePins = (type) =>
    apiData[0].comps.map((item) => ({
      ...item,
      lat: Number(item.latitude),
      lng: Number(item.longitude),
      customIcon:
        type === mapTypes[0].titleKey
          ? getMapPin('comp', formatPrice(item.price))
          : getMapPin('score', Math.round(item.AIRate)),
    }));

  const markerOnClick = async ({ item }) => {
    console.log('item:', item);
    if (!item.id || !item.propertyType) {
      return;
    }
    const isSuccess = await confirm({
      title: 'Listing Overview',
      message: <FormattedMessage id="app.common.compDialogMessage" />,
      confirmLabel: 'Yes',
    });

    if (isSuccess) {
      window.open(
        `${AppRoutes.protected.LISTINGS_DETAIL.path.replace(':id', item.id)}`,
        '_blank'
      );
    }
  };

  return (
    <>
      <RequestLoading loading={apiLoading} size="lg" margin="5" />
      <RequestResult type="error" message={apiError} />

      {!apiLoading && !apiError && apiData?.length > 0 && (
        <Row>
          <Col xs="12" className="mb-3 mb-lg-5">
            <Card className="overflow-hidden">
              <Row className="g-0">
                <Col lg="4">
                  <div className="position-relative">
                    <Card.Img
                      src={apiData[0].ImageURL}
                      className="w-100 h-100"
                      style={{
                        maxHeight: 300,
                        objectFit: 'cover',
                      }}
                    />
                    <Card.ImgOverlay
                      className="end-0 bottom-0"
                      style={{ left: 'auto', top: 'auto' }}
                    >
                      <Button as={Link} to="../gallery">
                        View Gallery
                      </Button>
                    </Card.ImgOverlay>
                  </div>
                </Col>
                <Col lg="8">
                  <Card.Body>
                    <div className="mb-3">
                      <div className="mb-3">
                        <span className="legend-indicator bg-success me-1" />
                        {apiData[0].status}
                      </div>
                      <h1 className="h4">
                        <span className="me-1">{apiData[0].address}</span>
                        <span className="fw-normal" />
                      </h1>
                    </div>
                    <div className="d-flex flex-column flex-md-row justify-content-between mb-5">
                      <div className="d-flex flex-column mb-3 mb-md-0 me-md-2">
                        <div className="h3">
                          <FormattedNumber
                            value={apiData[0].price}
                            // eslint-disable-next-line react/style-prop-object
                            style="currency"
                            currency="USD"
                            minimumFractionDigits={0}
                            maximumFractionDigits={0}
                          />
                        </div>
                        <div>
                          Est.{' '}
                          {calculateMonthlyMortgage(
                            apiData[0].price,
                            downPaymentPercent,
                            loanTermYears,
                            interestRate
                          )}
                          /mo
                          <a href="#!" target="_blank" className="ms-1" />
                        </div>
                      </div>
                      {apiData[0].bedrooms > 0 && (
                        <div className="d-flex flex-column mb-3 mb-md-0 me-md-2">
                          <div className="h3">{apiData[0].bedrooms}</div>
                          <span>Beds</span>
                        </div>
                      )}
                      {apiData[0].bathrooms > 0 && (
                        <div className="d-flex flex-column mb-3 mb-md-0 me-md-2">
                          <div className="h3">{apiData[0].bathrooms}</div>
                          <span>Baths</span>
                        </div>
                      )}
                      {apiData[0].totalSqFt > 0 && (
                        <div className="d-flex flex-column">
                          <div className="h3">
                            {' '}
                            <FormattedNumber
                              value={apiData[0].totalSqFt}
                              // eslint-disable-next-line react/style-prop-object

                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                            />
                          </div>
                          <span>Sq Ft</span>
                        </div>
                      )}
                    </div>

                    <div className="mb-5">
                      {Object.keys(apiData[0].tags).map((key) => (
                        <Tooltip
                          key={key}
                          content={formatMessage({
                            id: `app.toolTips.${key}`,
                          })}
                        >
                          <Badge
                            bg="light"
                            text="body"
                            className="me-2 mb-2 fs-6"
                          >
                            <FormattedMessage id={`app.common.${key}`} />
                          </Badge>
                        </Tooltip>
                      ))}
                    </div>
                    <div>
                      <div className="h3">About this home</div>
                      <ShowMoreText
                        text={apiData[0].description}
                        maxLine={5}
                        basedOn="words"
                      />
                    </div>
                  </Card.Body>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs="6" className="mb-3 mb-lg-5">
            <InfoPropertyDataCard id={id} />
          </Col>
          <Col xs="6" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Header className="card-header-content-between">
                <Card.Title bsPrefix="card-header-title" as="h4">
                  <FormattedMessage id="app.common.location" />
                </Card.Title>
                <Nav
                  variant="segment"
                  fill
                  as="ul"
                  activeKey={activeMapType}
                  onSelect={setActiveMapType}
                >
                  {mapTypes.map(({ titleKey }) => (
                    <Nav.Item key={titleKey} as="li">
                      <Nav.Link eventKey={titleKey}>
                        <FormattedMessage id={`app.common.${titleKey}`} />
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Card.Header>
              {activeMapType === mapTypes[0].titleKey ? (
                <GoogleMap
                  className="rounded-bottom"
                  height={500}
                  initialZoom={14}
                  initialMarks={[
                    {
                      lat: Number(apiData[0].latitude),
                      lng: Number(apiData[0].longitude),
                      customIcon: getMapPin(
                        'own',
                        formatPrice(apiData[0].price)
                      ),
                    },
                    ...apiData[0].gtfs_stops,
                    ...getPriceOrScorePins(mapTypes[0].titleKey),
                  ]}
                  initialCenter={{
                    lat: Number(apiData[0].latitude),
                    lng: Number(apiData[0].longitude),
                  }}
                  markerOnClick={markerOnClick}
                />
              ) : (
                <GoogleMap
                  className="rounded-bottom"
                  height={500}
                  initialZoom={14}
                  initialMarks={[
                    {
                      lat: Number(apiData[0].latitude),
                      lng: Number(apiData[0].longitude),
                      customIcon: getMapPin(
                        'score',
                        Math.round(apiData[0].AIRate)
                      ),
                    },
                    ...apiData[0].gtfs_stops,
                    ...getPriceOrScorePins(mapTypes[1].titleKey),
                  ]}
                  initialCenter={{
                    lat: Number(apiData[0].latitude),
                    lng: Number(apiData[0].longitude),
                  }}
                  markerOnClick={markerOnClick}
                />
              )}
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.aiRate" />
                  <Tooltip
                    content={formatMessage({ id: 'app.toolTips.aiRate' })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  <FormattedNumber
                    value={apiData[0].AIRate}
                    // eslint-disable-next-line react/style-prop-object
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Card.Title>

                <Badge bg={`soft-${advice.badgeColor}`} text={advice.badgeText}>
                  {advice.recommendation}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.price" />
                  <Tooltip
                    content={formatMessage({ id: 'app.toolTips.price' })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  <FormattedNumber
                    value={apiData[0].price}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Card.Title>
                <Badge
                  bg={`soft-${
                    Number(apiData[0].price) < ZIP_MEDIAN_HOME_PRICE
                      ? 'success'
                      : 'danger'
                  }`}
                  text={`${
                    Number(apiData[0].price) < ZIP_MEDIAN_HOME_PRICE
                      ? 'success'
                      : 'danger'
                  }`}
                >
                  Median home price average is&nbsp;
                  <FormattedNumber
                    value={ZIP_MEDIAN_HOME_PRICE}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                  .
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle as="h6">
                  <FormattedMessage id="app.common.buildingType" />
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].BuildingType}
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.daysOnMarket" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.daysOnMarket',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {getDateDifferenceAndColor(apiData[0].listingDate).badgeText}
                </Card.Title>

                <Badge
                  bg={`soft-${
                    Number(
                      getDateDifferenceAndColor(apiData[0].listingDate).days
                    ) < ZIP_MEDIAN_DAYS_ON_MARKET
                      ? 'success'
                      : 'danger'
                  }`}
                  text={`${
                    Number(
                      getDateDifferenceAndColor(apiData[0].listingDate).days
                    ) < ZIP_MEDIAN_DAYS_ON_MARKET
                      ? 'success'
                      : 'danger'
                  }`}
                >
                  Median days on maket is&nbsp;{' '}
                  <FormattedNumber
                    value={ZIP_MEDIAN_DAYS_ON_MARKET}
                    // eslint-disable-next-line react/style-prop-object
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.age" />
                  <Tooltip content={formatMessage({ id: 'app.toolTips.age' })}>
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {Number(moment().format('YYYY')) - apiData[0].yearBuilt}
                </Card.Title>
                <Badge
                  bg={`soft-${
                    Number(
                      Number(moment().format('YYYY')) - apiData[0].yearBuilt
                    ) < EconomicIndicators.US_AVERAGE_HOME_AGE
                      ? 'success'
                      : 'danger'
                  }`}
                  text={`${
                    Number(
                      Number(moment().format('YYYY')) - apiData[0].yearBuilt
                    ) < EconomicIndicators.US_AVERAGE_HOME_AGE
                      ? 'success'
                      : 'danger'
                  }`}
                >
                  {`The US average is ${EconomicIndicators.US_AVERAGE_HOME_AGE}.`}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.apnNumber" />
                  <Tooltip
                    content={formatMessage({ id: 'app.toolTips.apnNumber' })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].apnNumber ? (
                    apiData[0].apnNumber
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.lotSize" />
                  <Tooltip
                    content={formatMessage({ id: 'app.toolTips.lotSize' })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].lotSize ? (
                    apiData[0].lotSize
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.zoning" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.zoning',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].zoning ? (
                    apiData[0].zoning
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.owner" />
                  {apiData[0].owner && apiData[0].owner.length > 0 && (
                    <Tooltip
                      content={
                        <div className="d-flex flex-column">
                          <strong className="text-uppercase small">
                            <FormattedMessage id="app.common.allOwners" />
                          </strong>
                          {apiData[0].owner.map((item) => (
                            <span key={item.name}>{item.name}</span> // Anahtar (key) ekleme unutulmamalı
                          ))}
                        </div>
                      }
                    >
                      <i className="bi-info-circle-fill" />
                    </Tooltip>
                  )}
                </Card.Subtitle>
                {apiData[0].owner && apiData[0].owner.length > 0 ? (
                  <>
                    <Card.Title as="h2" className="text-inherit">
                      {`${apiData[0].owner[0].name} (${apiData[0].owner[0].total_properties})`}
                    </Card.Title>
                    <Badge bg="light" text="muted">
                      <FormattedMessage
                        id={`app.common.${apiData[0].owner[0].type}`}
                      />
                    </Badge>
                  </>
                ) : (
                  <Card.Title as="h2" className="text-inherit">
                    No owner data available
                  </Card.Title>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.equity" />
                  <Tooltip
                    content={formatMessage({ id: 'app.toolTips.equity' })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  {equity.equityPercentage.toFixed(2)}%
                </Card.Title>

                <Badge bg={`soft-${equity.color}`} text={`${equity.color}`}>
                  {equity.advice} &nbsp;{' '}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.rentersPercent" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.rentersPercent',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].rentersPercent ? (
                    apiData[0].rentersPercent
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
                <Badge bg="soft-info" text="info">
                  <i className="bi-info-circle-fill me-1" />
                  {`The US average is ${EconomicIndicators.US_AVERAGE_RENTERS_PERCENT}.%`}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.medianRentPrice" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.medianRentPrice',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  <FormattedNumber
                    value={apiData[0].MedianRentPrice}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Card.Title>
                <Badge
                  bg={`soft-${
                    Number(apiData[0].MedianRentPrice) >
                    EconomicIndicators.US_UMEDIAN_RENT_PRICE
                      ? 'success'
                      : 'danger'
                  }`}
                  text={`${
                    Number(apiData[0].MedianRentPrice) >
                    EconomicIndicators.US_UMEDIAN_RENT_PRICE
                      ? 'success'
                      : 'danger'
                  }`}
                >
                  The US average is&nbsp;
                  <FormattedNumber
                    value={EconomicIndicators.US_UMEDIAN_RENT_PRICE}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                  .
                </Badge>
              </Card.Body>
            </Card>
          </Col>{' '}
          <Col lg="6" className="mb-3 mb-lg-5">
            <BarChartCard
              field="demand_score"
              postalcode={apiData[0].zipCode}
            />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <WordCloudListing id={apiData[0].id} profileId={1} />
          </Col>
          <Col xs="12">
            <h3 className="mb-3">
              <FormattedMessage id="app.common.marketTrends" />
            </h3>
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <BarChartCard
              field="hotness_score"
              postalcode={apiData[0].zipCode}
            />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardMarketInventory zip={apiData[0].zipCode} />
          </Col>{' '}
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardMarketPriceIncrease zip={apiData[0].zipCode} />
          </Col>{' '}
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardMarketPricePerSquare zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCard zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardMarketRents zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardHomesSold zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardMedianDayOnMarket zip={apiData[0].zipCode} />
          </Col>
          <Col xs="12">
            <h3 className="mb-3">
              <FormattedMessage id="app.common.Demand" />
            </h3>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <ScoreChartCard
              value={Math.round(apiData[0].AIRate)}
              id={apiData[0].id}
            />
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Row className="h-100 justify-content-between">
              <Col className="mb-3">
                <Card className="card-hover-shadow h-100">
                  <Card.Body>
                    <Card.Subtitle
                      as="h6"
                      className="d-flex justify-content-between"
                    >
                      <FormattedMessage id="app.common.competitiveness" />
                      <Tooltip
                        content={formatMessage({
                          id: 'app.toolTips.competitiveness',
                        })}
                      >
                        <i className="bi-info-circle-fill" />
                      </Tooltip>
                    </Card.Subtitle>

                    <Card.Title
                      as="h2"
                      className="text-inherit"
                      style={{ fontSize: '6em' }}
                    >
                      {apiData[0].competitive_score}
                    </Card.Title>

                    <Badge
                      className="text-wrap text-start"
                      bg={`soft-${
                        getCompetitiveness(apiData[0].competitive_title).color
                      }`}
                      text={`${
                        getCompetitiveness(apiData[0].competitive_title).color
                      }`}
                    >
                      <i className="bi-info-circle-fill me-1" />
                      {getCompetitiveness(apiData[0].competitive_title).text}
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>
              <Col className="mb-3">
                <Card className="card-hover-shadow h-100">
                  <Card.Body>
                    <Card.Subtitle
                      as="h6"
                      className="d-flex justify-content-between"
                    >
                      <FormattedMessage id="app.common.competitiveness" />
                      <Tooltip
                        content={formatMessage({
                          id: 'app.toolTips.competitiveness',
                        })}
                      >
                        <i className="bi-info-circle-fill" />
                      </Tooltip>
                    </Card.Subtitle>

                    <Card.Title as="h2" className="text-inherit">
                      {apiData[0].competitive_title}
                    </Card.Title>

                    <Badge
                      className="text-wrap text-start"
                      bg={`soft-${
                        getCompetitiveness(apiData[0].competitive_title).color
                      }`}
                      text={`${
                        getCompetitiveness(apiData[0].competitive_title).color
                      }`}
                    >
                      <i className="bi-info-circle-fill me-1" />
                      {getCompetitiveness(apiData[0].competitive_title).text}
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>
              <Col className="mb-3">
                <Card className="card-hover-shadow h-100">
                  <Card.Body>
                    <Card.Subtitle
                      as="h6"
                      className="d-flex justify-content-between"
                    >
                      <FormattedMessage id="app.common.averageSellingDay" />
                      <Tooltip
                        content={formatMessage({
                          id: 'app.toolTips.averageSellingDay',
                        })}
                      >
                        <i className="bi-info-circle-fill" />
                      </Tooltip>
                    </Card.Subtitle>

                    <Card.Title
                      as="h2"
                      className="text-inherit"
                      style={{ fontSize: '4em' }}
                    >
                      {apiData[0].average_selling_day}
                    </Card.Title>
                    <Badge bg="soft-danger" text="danger">
                      <i className="bi-fire me-1" />
                      The hot homes go pending in around{' '}
                      {apiData[0].hot_average_selling_day} days
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>
              <Col>
                <Card className="card-hover-shadow h-100">
                  <Card.Body>
                    <Card.Subtitle
                      as="h6"
                      className="d-flex justify-content-between"
                    >
                      <FormattedMessage id="app.common.averagePriceChange" />
                      <Tooltip
                        content={formatMessage({
                          id: 'app.toolTips.averagePriceChange',
                        })}
                      >
                        <i className="bi-info-circle-fill" />
                      </Tooltip>
                    </Card.Subtitle>

                    <Card.Title
                      as="h2"
                      className="text-inherit"
                      style={{ fontSize: '3em' }}
                    >
                      {apiData[0].average_price_change}%
                    </Card.Title>
                    <Badge bg="soft-danger" text="danger">
                      <i className="bi-fire me-1" />
                      Hot homes can sell for about{' '}
                      {apiData[0].hot_average_price_change}% above list price
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <BarChartCard field="demand_score" postalcode={apiData[0].zip} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardSaleToListPrice zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <LineChartCardHomeSoldAboveList zip={apiData[0].zipCode} />
          </Col>
          <Col xs="12">
            <h3 className="mb-3">
              <FormattedMessage id="app.common.shortTermAnalysis" />
            </h3>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                {' '}
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.anyTypeDailyAveragePrice" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.anyTypeDailyAveragePrice',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  <FormattedNumber
                    value={apiData[0].AirbnbAnyTypeAveragePrice}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.roomDailyAveragePrice" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.roomDailyAveragePrice',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>
                <Card.Title as="h2" className="text-inherit">
                  <FormattedNumber
                    value={apiData[0].AirbnbRoomAveragePrice}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.entireHomeDailyAveragePrice" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.entireHomeDailyAveragePrice',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  <FormattedNumber
                    value={apiData[0].AirbnbEntireHomeAveragePrice}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency="USD"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.shortTermRentalNearby" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.shortTermRentalNearby',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].AirbnbAverageStay ? (
                    apiData[0].AirbnbAverageStay
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
              </Card.Body>
            </Card>
          </Col>
          <Col xs="12">
            <h3 className="mb-3">
              <FormattedMessage id="app.common.riskFactors" />
            </h3>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.flood" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.flood',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].flood_factor ? (
                    apiData[0].flood_factor
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
                <Badge
                  bg={`soft-${getRiskColor(apiData[0].flood_factor)}`}
                  text={`${getRiskColor(apiData[0].flood_factor)}`}
                  className="text-wrap text-start"
                >
                  <i className="bi-info-circle-fill me-1" />

                  {apiData[0].flood ? (
                    apiData[0].flood
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.fire" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.fire',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].fire_factor ? (
                    apiData[0].fire_factor
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
                <Badge
                  bg={`soft-${getRiskColor(apiData[0].fire_factor)}`}
                  text={`${getRiskColor(apiData[0].fire_factor)}`}
                  className="text-wrap"
                >
                  <i className="bi-info-circle-fill me-1" />

                  {apiData[0].fire ? (
                    apiData[0].fire
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.wind" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.wind',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].wind_factor ? (
                    apiData[0].wind_factor
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
                <Badge
                  bg={`soft-${getRiskColor(apiData[0].wind_factor)}`}
                  text={`${getRiskColor(apiData[0].wind_factor)}`}
                  className="text-wrap"
                >
                  <i className="bi-info-circle-fill me-1" />

                  {apiData[0].wind ? (
                    apiData[0].wind
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col md="6" lg="3" className="mb-3 mb-lg-5">
            <Card className="card-hover-shadow h-100">
              <Card.Body>
                <Card.Subtitle
                  as="h6"
                  className="d-flex justify-content-between"
                >
                  <FormattedMessage id="app.common.heat" />
                  <Tooltip
                    content={formatMessage({
                      id: 'app.toolTips.heat',
                    })}
                  >
                    <i className="bi-info-circle-fill" />
                  </Tooltip>
                </Card.Subtitle>

                <Card.Title as="h2" className="text-inherit">
                  {apiData[0].heat_factor ? (
                    apiData[0].heat_factor
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Card.Title>
                <Badge
                  bg={`soft-${getRiskColor(apiData[0].heat_factor)}`}
                  text={`${getRiskColor(apiData[0].heat_factor)}`}
                  className="text-wrap"
                >
                  <i className="bi-info-circle-fill me-1" />

                  {apiData[0].heat ? (
                    apiData[0].heat
                  ) : (
                    <FormattedMessage id="app.common.n/a" />
                  )}
                </Badge>
              </Card.Body>
            </Card>
          </Col>
          <Col xs="12">
            <h3 className="mb-3">
              <FormattedMessage id="app.common.demography" />
            </h3>
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <DoughnutChartCard zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <HalfDoughnutChartCard zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <DoughnutChartCardRace zip={apiData[0].zipCode} />
          </Col>
          <Col lg="6" className="mb-3 mb-lg-5">
            <HalfDoughnutChartCardVoting zip={apiData[0].zipCode} />
          </Col>
          <Col xs="12">
            <MortgageCalculator
              price={apiData[0].price}
              initialDownPayment={apiData[0].price * (downPaymentPercent / 100)}
            />
          </Col>
        </Row>
      )}
    </>
  );
}

export default Info;
